import {
  getGetSurchargesQueryKey,
  useDeleteSurchargesId,
  useGetSurcharges,
  useGetVatCategories,
  usePatchSurchargesId,
  usePostSurcharges,
} from '@agos/api-client/menu'
import { T, formatCents, tt, useAuth, useModalFocus } from '@agos/shared'
import { ConfirmDialog, PageHeader, Spinner, StatusBadge, cn, useToast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, PackagePlus, Plus, Trash2, X } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import { mayWrite, readOnlyReason } from '../write-access'
import {
  EMPTY_DRAFT,
  categoryChoices,
  categoryChoicesOf,
  categoryLabel,
  categoryRequirementNote,
  categoryWarning,
  chosenCategory,
  deletionNote,
  draftOf,
  emptyNote,
  lineExample,
  problems,
  surchargePayload,
  surchargesOf,
  type Draft,
  type Surcharge,
} from './surcharges'

/**
 * `/surcharges` — §9.5's deposits and per-item surcharges. **A justified EXTRA**, citing that section.
 *
 * Five endpoints, zero rows: nothing has ever been able to create one. The screen's whole reason for
 * existing is **FB-187** — the category a container deposit legally takes (`D`, outside VAT) is one the
 * register has no confirmed index for, so a product carrying it stops printing. That is stated where the
 * choice is made, and the fix named is the missing index rather than a different tax.
 */
export function SurchargesPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { user, hasPermission } = useAuth()
  // `POST`/`PATCH`/`DELETE /surcharges` ask `fiskal_settings` (§9.758, BACKEND.md §10A.413): a surcharge decides what
  // tax the register is told, as the register's own settings do. The attachment endpoint on a product is
  // `{main_admin, admin}`, so whoever may put a surcharge on a product may still not invent one without the right -
  // the server's split and not this screen's.
  const writable = mayWrite('surcharges', user?.systemRole, hasPermission)
  const readOnly = readOnlyReason('surcharges', user?.systemRole, hasPermission)

  const { data, isLoading } = useGetSurcharges()
  const categoryQuery = useGetVatCategories()
  const surcharges = useMemo(() => surchargesOf(data), [data])
  /* §9.598 - **resolving and offering are two questions.** Every category the server answers is kept for
     resolution, so a row whose rate the franchise has since retired is still named in the list rather than
     going blank; what the dropdown *offers* is narrowed below, once the editing row is known. */
  const categories = useMemo(() => categoryChoicesOf(categoryQuery.data), [categoryQuery.data])

  const [editing, setEditing] = useState<{ id: number | null; draft: Draft } | null>(null)
  /* §9.487, `TODO.md` 3c - the trap and the Escape. Unconditional: the form's submit is a separate button,
     so closing mid-typing loses a draft nobody committed, which is what its own cancel does. */
  const panelRef = useRef<HTMLElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, () => setEditing(null))
  const [deleting, setDeleting] = useState<Surcharge | null>(null)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetSurchargesQueryKey() })
  }

  const create = usePostSurcharges({
    mutation: {
      onSuccess: () => {
        refresh()
        setEditing(null)
        toast({ title: tt('Príplatok pridaný') })
      },
      onError: (error) => toast({ variant: 'destructive', title: messageOf(error) }),
    },
  })

  const update = usePatchSurchargesId({
    mutation: {
      onSuccess: () => {
        refresh()
        setEditing(null)
        toast({ title: tt('Príplatok upravený') })
      },
      onError: (error) => toast({ variant: 'destructive', title: messageOf(error) }),
    },
  })

  const remove = useDeleteSurchargesId({
    mutation: {
      onSuccess: () => {
        refresh()
        setDeleting(null)
        toast({ title: tt('Príplatok zmazaný') })
      },
      // The server refuses one that is on an order, and its refusal is the answer — it knows the order
      // lines and this screen does not.
      onError: (error) => toast({ variant: 'destructive', title: messageOf(error) }),
    },
  })

  const found = editing === null ? [] : problems(editing.draft)
  const chosen = editing === null ? null : chosenCategory(editing.draft.vatCategoryId, categories)
  /* The switched-on rates, plus the one this row already carries - §9.598. A `<select>` whose value
     matches no `<option>` renders blank, so narrowing alone put an empty box where a tax rate is. */
  const categoryOptions = useMemo(
    () => categoryChoices(categories, editing?.draft.vatCategoryId ?? null),
    [categories, editing],
  )
  const warning = categoryWarning(chosen)

  return (
    <div className="space-y-6" data-surcharges>
      <PageHeader
        title={tt('Príplatky a zálohy')}
        description={tt('Samostatný riadok na doklade - záloha za obal, balné a podobne.')}
        actions={
          writable ? (
            <button
              type="button"
              data-add-surcharge
              onClick={() => setEditing({ id: null, draft: EMPTY_DRAFT })}
              className="min-h-[40px] px-4 rounded-xl bg-primary text-primary-foreground font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
            >
              <Plus className="h-4 w-4" /> <T>Pridať príplatok</T>
            </button>
          ) : undefined
        }
      />

      {readOnly !== null && (
        <p className="text-xs text-muted-foreground" data-read-only>
          {readOnly}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : surcharges.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-6 text-center space-y-2" data-surcharges-empty>
          <PackagePlus className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground max-w-lg mx-auto">{emptyNote()}</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {surcharges.map((surcharge) => {
            const category = chosenCategory(surcharge.vatCategoryId, categories)
            return (
              <li
                key={surcharge.id}
                data-surcharge={surcharge.id}
                className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">
                    {surcharge.name}
                    {!surcharge.isActive && (
                      <span className="ml-2">
                        <StatusBadge color="zinc" label={tt('vypnutý')} />
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatCents(surcharge.amountCents)}
                    {category !== null && ` · ${categoryLabel(category)}`}
                  </p>
                  {category !== null && !category.printable && (
                    <p className="text-[11px] text-amber-400/90 pt-1" data-surcharge-unprintable={surcharge.id}>
                      {categoryWarning(category)}
                    </p>
                  )}
                </div>
                {writable && (
                  <>
                    <button
                      type="button"
                      data-edit-surcharge={surcharge.id}
                      onClick={() => setEditing({ id: surcharge.id, draft: draftOf(surcharge) })}
                      className="min-h-[40px] px-3 rounded-xl border border-border text-sm font-semibold hover:bg-muted"
                    >
                      <T>Upraviť</T>
                    </button>
                    <button
                      type="button"
                      data-delete-surcharge={surcharge.id}
                      onClick={() => setDeleting(surcharge)}
                      aria-label={tt('Zmazať')}
                      className="min-h-[40px] min-w-[40px] flex items-center justify-center rounded-xl border border-border text-destructive hover:bg-muted"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {editing !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-surcharge-form>
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label={tt('Zavrieť')}
            onClick={() => setEditing(null)}
          />
          {/* §9.487 - the role is on the `<section>` and not the wrapper, because the wrapper holds the
              dismiss scrim as a sibling: a role there would put the dismiss control inside the dialog it
              dismisses. §9.469's reasoning, and §9.485's. */}
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="surcharge-form-title"
            ref={panelRef}
            onKeyDown={onKeyDown}
            className="relative w-full max-w-lg max-h-[92dvh] overflow-y-auto rounded-2xl bg-background border border-border p-5 space-y-4"
          >
            <div className="flex items-center justify-between">
              <h2 id="surcharge-form-title" className="text-base font-bold">
                {editing.id === null ? <T>Nový príplatok</T> : <T>Úprava príplatku</T>}
              </h2>
              <button
                type="button"
                data-dialog-cancel
                onClick={() => setEditing(null)}
                aria-label={tt('Zavrieť')}
                className="min-h-[40px] min-w-[40px] flex items-center justify-center rounded-xl hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">
                <T>Názov</T>
              </span>
              <input
                value={editing.draft.name}
                onChange={(event) =>
                  setEditing({ ...editing, draft: { ...editing.draft, name: event.target.value } })
                }
                data-surcharge-name
                placeholder={tt('Zálohovaný obal')}
                className="w-full min-h-[44px] rounded-xl bg-card border border-border px-3 text-sm outline-none focus:border-primary"
              />
            </label>

            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">
                <T>Suma</T>
              </span>
              <input
                value={editing.draft.amount}
                onChange={(event) =>
                  setEditing({ ...editing, draft: { ...editing.draft, amount: event.target.value } })
                }
                inputMode="decimal"
                data-surcharge-amount
                placeholder="0,15"
                className="w-full min-h-[44px] rounded-xl bg-card border border-border px-3 text-sm tabular-nums outline-none focus:border-primary"
              />
            </label>

            <div className="space-y-1">
              <label className="block space-y-1">
                <span className="text-xs text-muted-foreground">
                  <T>Kategória DPH</T>
                </span>
                <select
                  value={editing.draft.vatCategoryId === null ? '' : String(editing.draft.vatCategoryId)}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      draft: {
                        ...editing.draft,
                        vatCategoryId: event.target.value === '' ? null : Number(event.target.value),
                      },
                    })
                  }
                  data-surcharge-vat
                  className="w-full min-h-[44px] rounded-xl bg-card border border-border px-3 text-sm outline-none focus:border-primary"
                >
                  <option value="">{tt('- vyberte -')}</option>
                  {categoryOptions.map((choice) => (
                    <option key={choice.id} value={choice.id}>
                      {categoryLabel(choice)}
                    </option>
                  ))}
                </select>
              </label>
              <p className="text-[11px] text-muted-foreground" data-vat-requirement>
                {categoryRequirementNote()}
              </p>
              {warning !== '' && (
                <p className="text-[11px] text-amber-400/90 flex gap-1.5" data-vat-warning>
                  <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" />
                  <span>{warning}</span>
                </p>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editing.draft.isActive}
                onChange={(event) =>
                  setEditing({ ...editing, draft: { ...editing.draft, isActive: event.target.checked } })
                }
                data-surcharge-active
                className="h-4 w-4"
              />
              <T>Účtovať</T>
            </label>

            {/* What the receipt will show, since a surcharge is charged per unit of what carried it. */}
            <p className="text-[11px] text-muted-foreground" data-surcharge-example>
              {lineExample(editing.draft, chosen)}
            </p>

            {found.length > 0 && (
              <ul className="text-[11px] text-destructive space-y-1" data-surcharge-problems>
                {found.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            )}

            <button
              type="button"
              data-save-surcharge
              disabled={found.length > 0 || create.isPending || update.isPending}
              onClick={() => {
                const payload = surchargePayload(editing.draft)
                if (editing.id === null) create.mutate({ data: payload })
                else update.mutate({ id: editing.id, data: payload })
              }}
              className={cn(
                'w-full min-h-[48px] rounded-xl bg-primary text-primary-foreground font-bold',
                'hover:opacity-90 transition-opacity disabled:opacity-50',
              )}
            >
              {create.isPending || update.isPending ? <Spinner /> : <T>Uložiť</T>}
            </button>
          </section>
        </div>
      )}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting !== null) remove.mutate({ id: deleting.id })
        }}
        title={tt('Zmazať príplatok?')}
        description={deleting === null ? '' : `${deleting.name} · ${deletionNote()}`}
        confirmLabel={tt('Zmazať')}
        variant="danger"
        loading={remove.isPending}
      />
    </div>
  )
}

/** The server's own message where there is one — its refusals explain themselves better than a generic. */
function messageOf(error: unknown): string {
  const message = (error as { error?: string; message?: string } | null)?.error
  return typeof message === 'string' && message !== '' ? message : tt('Nepodarilo sa uložiť')
}
